import * as React from "react";
import { cn } from "@/v2/lib/utils";
import type { RestrictedWord } from "@/shared/cache";
import type { RestrictedWordMatch } from "@/shared/utils/restrictedWords";
import {
  detectRestrictedWords,
  mergeOverlappingMatches,
} from "@/shared/utils/restrictedWords";

export interface NarrativeFieldHandle {
  generate: () => void;
  undo: () => void;
  isGenerating: boolean;
  canUndo: boolean;
}

interface NarrativeFieldProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: () => void;
  error?: string;
  restrictedWords: RestrictedWord[];
  textareaRef?: React.Ref<HTMLTextAreaElement>;
  onGenerate?: () => Promise<string>;
  onDirectChange?: (value: string) => void;
  /** Ref for imperative access to generate/undo actions */
  actionRef?: React.Ref<NarrativeFieldHandle>;
  /** Called when generating state changes — allows parent to render buttons */
  onStateChange?: (state: { isGenerating: boolean; canUndo: boolean }) => void;
}

function buildHighlightSegments(
  text: string,
  matches: RestrictedWordMatch[],
): React.ReactNode[] {
  if (!matches.length) return [text];

  const segments: React.ReactNode[] = [];
  let lastIndex = 0;

  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    if (match.start > lastIndex) {
      segments.push(text.slice(lastIndex, match.start));
    }
    const matchedText = text.slice(match.start, match.end);
    const markClass =
      match.severity === "block"
        ? "bg-red-200/70 dark:bg-red-500/25"
        : "bg-amber-200/70 dark:bg-amber-500/25";
    segments.push(
      <mark key={`m-${i}`} className={markClass}>
        {matchedText}
      </mark>,
    );
    lastIndex = match.end;
  }
  if (lastIndex < text.length) {
    segments.push(text.slice(lastIndex));
  }

  return segments;
}

export function NarrativeField({
  value,
  onChange,
  onBlur,
  error,
  restrictedWords,
  textareaRef: externalRef,
  onGenerate,
  onDirectChange,
  actionRef,
  onStateChange,
}: NarrativeFieldProps) {
  const internalRef = React.useRef<HTMLTextAreaElement>(null);
  const textareaRef = (externalRef as React.RefObject<HTMLTextAreaElement>) ?? internalRef;
  const mirrorRef = React.useRef<HTMLDivElement>(null);

  const [isGenerating, setIsGenerating] = React.useState(false);
  const [previousValue, setPreviousValue] = React.useState<string | null>(null);
  const streamingRef = React.useRef<{ cancelled: boolean; intervalId: ReturnType<typeof setInterval> } | null>(null);

  // Cleanup streaming on unmount
  React.useEffect(() => {
    return () => {
      if (streamingRef.current) {
        streamingRef.current.cancelled = true;
        clearInterval(streamingRef.current.intervalId);
      }
    };
  }, []);

  const matches = React.useMemo(
    () => streamingRef.current
      ? []
      : mergeOverlappingMatches(detectRestrictedWords(value, restrictedWords)),
    [value, restrictedWords],
  );

  const handleGenerate = React.useCallback(async () => {
    if (!onGenerate || !onDirectChange) return;
    setPreviousValue(value);
    setIsGenerating(true);
    try {
      const generated = await onGenerate();
      const tokens = generated.split(/(\s+)/);
      let index = 0;
      let accumulated = "";
      streamingRef.current = {
        cancelled: false,
        intervalId: setInterval(() => {
          if (streamingRef.current?.cancelled) {
            clearInterval(streamingRef.current.intervalId);
            streamingRef.current = null;
            return;
          }
          while (index < tokens.length) {
            accumulated += tokens[index];
            index++;
            if (tokens[index - 1]?.trim()) break;
          }
          onDirectChange(accumulated);
          if (index >= tokens.length) {
            clearInterval(streamingRef.current!.intervalId);
            streamingRef.current = null;
            setIsGenerating(false);
          }
        }, 50),
      };
    } catch {
      setIsGenerating(false);
      setPreviousValue(null);
    }
  }, [onGenerate, onDirectChange, value]);

  const handleUndo = React.useCallback(() => {
    if (streamingRef.current) {
      streamingRef.current.cancelled = true;
      clearInterval(streamingRef.current.intervalId);
      streamingRef.current = null;
      setIsGenerating(false);
    }
    if (previousValue !== null && onDirectChange) {
      onDirectChange(previousValue);
      setPreviousValue(null);
    }
  }, [previousValue, onDirectChange]);

  const canUndo = previousValue !== null && !isGenerating;

  React.useImperativeHandle(actionRef, () => ({
    generate: handleGenerate,
    undo: handleUndo,
    isGenerating,
    canUndo,
  }), [handleGenerate, handleUndo, isGenerating, canUndo]);

  React.useEffect(() => {
    onStateChange?.({ isGenerating, canUndo });
  }, [isGenerating, canUndo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync scroll between textarea and mirror
  const handleScroll = React.useCallback(() => {
    if (textareaRef.current && mirrorRef.current) {
      mirrorRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  // Auto-resize
  React.useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.max(120, ta.scrollHeight)}px`;
  }, [value]);

  const highlightSegments = buildHighlightSegments(value, matches);

  // Sort matches: blocks first, then warnings
  const sortedMatches = React.useMemo(() => {
    return [...matches].sort((a, b) => {
      if (a.severity === "block" && b.severity !== "block") return -1;
      if (a.severity !== "block" && b.severity === "block") return 1;
      return a.start - b.start;
    });
  }, [matches]);

  const sharedStyle =
    "text-sm leading-relaxed px-3 py-2 w-full rounded-md border";

  return (
    <div>
      {/* Overlay container */}
      <div className="relative grid" style={{ gridTemplate: "1fr / 1fr" }}>
        {/* Mirror div with highlights */}
        <div
          ref={mirrorRef}
          aria-hidden
          className={cn(
            sharedStyle,
            "border-transparent whitespace-pre-wrap break-words overflow-hidden pointer-events-none",
            "text-transparent [&_mark]:text-transparent",
          )}
          style={{ gridArea: "1 / 1" }}
        >
          {highlightSegments}
          {/* Trailing newline so mirror matches textarea height */}
          {"\n"}
        </div>

        {/* Actual textarea */}
        <textarea
          ref={textareaRef}
          id="description"
          placeholder="Describe the work performed..."
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onScroll={handleScroll}
          readOnly={isGenerating}
          className={cn(
            sharedStyle,
            "bg-transparent resize-none overflow-hidden caret-current",
            "text-foreground border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            error && "border-red-500",
          )}
          style={{ gridArea: "1 / 1", minHeight: 120 }}
        />
      </div>

      {/* Error message */}
      {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}

      {/* Match summary */}
      {sortedMatches.length > 0 && (
        <div className="mt-2 space-y-1 text-xs max-h-32 overflow-y-auto">
          {sortedMatches.map((match, i) => {
            const matchedText = value.slice(match.start, match.end);
            const truncated =
              matchedText.length > 30
                ? matchedText.slice(0, 30) + "..."
                : matchedText;
            return (
              <div key={`${match.start}-${i}`} className="flex items-start gap-1.5">
                <span
                  className={cn(
                    "mt-1 shrink-0 size-2 rounded-full",
                    match.severity === "block" ? "bg-red-500" : "bg-amber-500",
                  )}
                />
                <span>
                  <span className="font-medium">{match.displayName}</span>
                  {" "}
                  <span className="text-muted-foreground">
                    &ldquo;{truncated}&rdquo;
                  </span>
                  {match.suggestion && (
                    <span className="block text-muted-foreground">
                      {match.suggestion}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
